import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Printer, Mail, Link as LinkIcon, QrCode, Share2, ChevronRight, ArrowLeft } from 'lucide-react';
export function CAGuidePage() {
  const [checklist, setChecklist] = useState([false, false, false]);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [progress, setProgress] = useState(0);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleCheck = (index: number) => {
    const newChecklist = [...checklist];
    newChecklist[index] = !newChecklist[index];
    setChecklist(newChecklist);
    setHasInteracted(true);
    // Update progress based on checklist (up to 50% before completion)
    const completedCount = newChecklist.filter(Boolean).length;
    if (!isCompleted) {
      setProgress(Math.round(completedCount / newChecklist.length * 50));
    }
  };
  const handleComplete = () => {
    setIsCompleted(true);
    setProgress(100);
    // Small delay to allow render before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };
  return <div className="min-h-screen w-full bg-white text-[#1a0647]" data-id="element-769" data-mp="2pxwveifmmlb3">
      {/* Simple Navigation Bar */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm" data-id="element-770" data-mp="3mlt6j7saooex">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-771" data-mp="3hpchmbd9u60s">
          <Link to="/resources" className="text-gray-600 hover:text-[#8528e2] font-manrope font-semibold text-base transition-colors flex items-center gap-2 min-h-[44px] min-w-[44px]" data-id="element-772" data-mp="3cw04oyhyfnnv">
            <ArrowLeft size={20} aria-hidden="true" data-id="element-773" data-mp="99j4m6i5fcoh" />
            Back to Resources
          </Link>
          <img src="https://cdn.magicpatterns.com/uploads/8EVxVy8dkAVEdmFcPkswyr/Caveo_2026_Logo.png" alt="Caveo Logo" className="h-6 w-auto" data-id="element-774" data-mp="4fesz9xxugh2" />
        </div>
      </header>

      <main data-id="element-775" data-mp="23qat613bxjvr">
        {/* 1. Module Header (Hero Block) */}
        <section className="bg-[#1a0647] text-white py-12 md:py-16 px-4 md:px-8" data-id="element-776" data-mp="1oz0r7yibx0p6">
          <div className="max-w-3xl mx-auto" data-id="element-777" data-mp="1txlhp67s460f">
            <h1 className="font-manrope text-3xl md:text-5xl font-bold mb-4 text-white leading-tight" data-id="element-778" data-mp="1twabz0urwo6o">
              The California Resident's Guide to Privacy & Opt-Out
            </h1>
            <p className="font-inter text-lg md:text-xl mb-8 text-gray-200 leading-relaxed" data-id="element-779" data-mp="1kvvphy62p31l">
              Learn how to use California's new DROP tool and OOPS signal to
              stop data brokers from selling your family's personal information.
            </p>

            <div className="inline-flex items-center gap-2 bg-[#01ced1] text-[#1a0647] px-4 py-2 rounded-full font-manrope font-bold text-base mb-10 min-h-[44px]" data-id="element-780" data-mp="30fs1kntp5k15">
              <span aria-hidden="true" data-id="element-781" data-mp="tq6w5emabqn5">⏱️</span> 12 minutes to complete
            </div>

            <div className="space-y-3 bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-782" data-mp="ordk7qmnc7p3">
              <div className="flex justify-between font-manrope font-semibold text-base" data-id="element-783" data-mp="opiwk4jzq1hb">
                <span id="progress-label" data-id="element-784" data-mp="13ickmedbvpxl">Your progress</span>
                <span data-id="element-785" data-mp="yorluk3wurxc">{progress}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-labelledby="progress-label" data-id="element-786" data-mp="1cgob8ge72nq1">
                <div className="bg-[#214ae9] h-full transition-all duration-700 ease-out" style={{
                width: `${progress}%`
              }} data-id="element-787" data-mp="1hfj4yddp9wox" />
              </div>
            </div>
          </div>
        </section>

        {/* 2. The Hook (Story Block) */}
        <section className="bg-[#eae6dc] py-12 md:py-16 px-4 md:px-8" data-id="element-788" data-mp="3knrsaazvgvoz">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-6 items-start" data-id="element-789" data-mp="3pn4p9okgjj5a">
            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-3xl border border-gray-200" aria-hidden="true" data-id="element-790" data-mp="3un0lh1mwy6aa">
              🏠
            </div>
            <div className="flex-1 space-y-4" data-id="element-791" data-mp="31zmwbppgif0q">
              <p className="font-inter text-gray-700 text-lg leading-relaxed" data-id="element-792" data-mp="36wwsdo7cpoxj">
                Imagine opening your mailbox to find a catalog for a medical
                condition you only searched for once, late at night. Or worse,
                your teenager starts receiving texts from marketers who somehow
                know their exact age and location. Data brokers quietly collect
                and sell this information every day.
              </p>
              <p className="font-inter text-[#1a0647] text-xl font-bold leading-relaxed" data-id="element-793" data-mp="2o4kga9qwb9a7">
                In this module, you will learn exactly how to use California's
                free tools to force these companies to delete your family's
                data.
              </p>
            </div>
          </div>
        </section>

        {/* 3. Concept Cards (One Idea Per Card) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-794" data-mp="rb9nas236kd9">
          <div className="max-w-4xl mx-auto" data-id="element-795" data-mp="mcyd2g7vq7ui">
            <h2 className="sr-only" data-id="element-796" data-mp="mesx7sbbtvxj">Key Concepts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-797" data-mp="hkx1yxmg6n62">
              {/* Card 1 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-798" data-mp="djl64aczd5pf">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-799" data-mp="8mur7qcwwg5l">
                  The DROP Tool
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-800" data-mp="3usi2f45fj64a">
                  The Delete Request and Opt-Out Platform (DROP) is a free state
                  tool that sends a single deletion request to over 500
                  registered data brokers at once. Instead of emailing hundreds
                  of companies, you fill out one form.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-801" data-mp="3g5t2x2b6gsxw">
                  Think of this like a universal "Do Not Call" registry, but for
                  your personal data.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-802" data-mp="cr6smi3cc8le">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-803" data-mp="2z12zf0ka39d">
                  The OOPS Signal
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-804" data-mp="qo24eiaz8e55">
                  The Opt-Out Preference Signal (OOPS) is a setting in your web
                  browser that automatically tells every website you visit not
                  to sell or share your data. You turn it on once, and it works
                  quietly in the background.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-805" data-mp="2puqzrcfs3502">
                  Think of this like a digital "No Soliciting" sign permanently
                  hung on your front door.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-806" data-mp="1s8jtly3tt4ll">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-807" data-mp="2762g0tt175pv">
                  Data Brokers
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-808" data-mp="228kidzkgscx6">
                  These are companies that collect your public records, purchase
                  history, and online activity, package it into a profile, and
                  sell it to advertisers or other businesses.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-809" data-mp="1fx9el1snqdfh">
                  Think of them like digital gossips who profit from sharing
                  your family's secrets.
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-810" data-mp="1ppf0puw8a0mr">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-811" data-mp="1ynqzik7904qa">
                  Your Right to Delete
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-812" data-mp="23o6chanmba7q">
                  Under California law, you have the right to demand that
                  businesses delete the personal information they have collected
                  about you, with a few legal exceptions.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-813" data-mp="ea9x8oo9vy23">
                  Think of this like having the legal authority to shred someone
                  else's file on you.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Practice Activity (Interactive Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white border-t border-gray-100" data-id="element-814" data-mp="3o6qocyljllit">
          <div className="max-w-3xl mx-auto" data-id="element-815" data-mp="pmquivyn9jx9">
            <h2 className="font-manrope text-[#1a0647] text-3xl md:text-4xl font-bold mb-6" data-id="element-816" data-mp="plfqcv1aea5m">
              Try It Now
            </h2>
            <p className="font-inter text-gray-700 text-lg mb-10 leading-relaxed" data-id="element-817" data-mp="fp2ubqi9rk7p">
              Before you submit your official request, let's make sure you have
              everything you need. Tap each step as you complete it.
            </p>

            <div className="space-y-4" role="group" aria-label="Preparation Checklist" data-id="element-818" data-mp="30h4n85lm0w86">
              {['Check if your current web browser supports the OOPS signal (like Brave or Firefox) or if you need an extension.', "Gather the information you'll need for DROP: your full name, previous addresses, and old email accounts.", "Locate your Mobile Advertising ID (MAID) in your phone's privacy settings."].map((item, i) => <button key={i} onClick={() => handleCheck(i)} className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 flex items-start gap-5 min-h-[44px] ${checklist[i] ? 'border-[#01ced1] bg-[#01ced1]/5 shadow-sm' : 'border-gray-200 bg-[#f8f8f8] hover:border-[#8528e2]/40 hover:bg-white'}`} aria-pressed={checklist[i]} data-id="element-819" data-mp="1hjarx66hitu3" data-mpi={i}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 mt-0.5 transition-colors ${checklist[i] ? 'bg-[#01ced1] border-[#01ced1] text-white' : 'border-gray-300 text-transparent bg-white'}`} data-id="element-820" data-mp="3t9wmibwabu6p">
                    <CheckCircle2 size={20} aria-hidden="true" data-id="element-821" data-mp="28q0z21yob6ty" />
                  </div>
                  <span className={`font-inter text-lg leading-relaxed ${checklist[i] ? 'text-[#1a0647] font-semibold' : 'text-gray-700'}`} data-id="element-822" data-mp="1u1ekw3ks8l4b">
                    {item}
                  </span>
                </button>)}
            </div>
          </div>
        </section>

        {/* 5. Honesty Gate (Pre-Commitment Block) */}
        <section className="py-8 px-4 md:px-8 bg-white" data-id="element-823" data-mp="1b3tx6ztr7sla">
          <div className="max-w-3xl mx-auto" data-id="element-824" data-mp="1b5esirnw7fk7">
            <div className="border-l-4 border-[#8528e2] bg-[#8528e2]/5 p-6 md:p-8 rounded-r-xl mb-8" data-id="element-825" data-mp="1g255350hnt0v">
              <p className="font-inter text-[#1a0647] text-lg leading-relaxed font-medium" data-id="element-826" data-mp="35hczemyqbzcx">
                Before you confirm: this step matters for your family's
                protection. Please only mark it complete if you actually did it.
                It's completely okay to come back to this later.
              </p>
            </div>

            {hasInteracted && !isCompleted && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-827" data-mp="2z0ta9ypgwdjg">
                <button onClick={handleComplete} className="bg-[#8528e2] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#b063ff] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center w-full md:w-auto shadow-md" data-id="element-828" data-mp="2v010n4coo6mn">
                  I completed this step
                </button>
              </div>}
          </div>
        </section>

        {/* 6. Reflection Prompt (Personal Connection Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-829" data-mp="ebnhnelg62al">
          <div className="max-w-3xl mx-auto" data-id="element-830" data-mp="ecyknc72j60n">
            <div className="bg-[#f8f8f8] border border-gray-200 p-8 md:p-10 rounded-2xl shadow-sm" data-id="element-831" data-mp="9jcfe538i4ev">
              <div className="inline-block bg-[#eae6dc] text-[#1a0647] font-manrope font-bold px-4 py-1.5 rounded-md text-sm mb-6 uppercase tracking-wider" data-id="element-832" data-mp="1s86v8xxu4w8r">
                Reflect for a Moment
              </div>
              <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold mb-8 leading-tight" data-id="element-833" data-mp="216a5uz7a88ls">
                Which of these steps feels hardest for you right now, and what's
                one thing that might make it easier?
              </h3>
              <textarea value={reflectionText} onChange={e => setReflectionText(e.target.value)} placeholder="Type your thoughts here..." className="w-full bg-white border border-gray-300 rounded-xl p-5 font-inter text-gray-700 text-lg min-h-[160px] focus:outline-none focus:ring-2 focus:ring-[#8528e2] focus:border-transparent resize-y shadow-inner" aria-label="Reflection input" data-id="element-834" data-mp="2g4knv5q347gc" />
            </div>
          </div>
        </section>

        {/* 7. Takeaway Block (Action Anchor) */}
        <section className="bg-[#1a0647] text-white py-20 px-4 md:px-8" data-id="element-835" data-mp="2g9s4rjx4o3kl">
          <div className="max-w-3xl mx-auto text-center" data-id="element-836" data-mp="2g9if5xfzvyff">
            <p className="font-inter text-2xl md:text-3xl font-bold leading-relaxed mb-12" data-id="element-837" data-mp="2uyy3l6s1zrry">
              The single most important action you can take today is to submit
              your DROP request at consumer.drop.privacy.ca.gov.
            </p>

            <div className="flex flex-wrap justify-center gap-4 md:gap-6" data-id="element-838" data-mp="22bu2hcw2zxlg">
              <button className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-839" data-mp="1xch8m86slq9d">
                <Printer size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-840" data-mp="277iw6zlf6k9u" />
                <span className="font-manrope text-sm font-semibold" data-id="element-841" data-mp="3iosc5c5cau6e">
                  Print
                </span>
              </button>
              <button className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-842" data-mp="3q66zfnqu8ups">
                <Mail size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-843" data-mp="3h8esp7z6mve0" />
                <span className="font-manrope text-sm font-semibold" data-id="element-844" data-mp="3l7x6ljsl667c">
                  Email to myself
                </span>
              </button>
              <button className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-845" data-mp="cuygdd3y99rm">
                <LinkIcon size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-846" data-mp="3yh589vl5bvv" />
                <span className="font-manrope text-sm font-semibold" data-id="element-847" data-mp="vqon1dmtuubu">
                  Copy link
                </span>
              </button>
              <button className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-848" data-mp="10kkj2ajj674d">
                <QrCode size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-849" data-mp="16aqogwtteqcl" />
                <span className="font-manrope text-sm font-semibold" data-id="element-850" data-mp="wfgk42fy26vn">
                  QR code
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* 8. Completion Confirmation */}
        {isCompleted && <section className="bg-[#01ced1] py-16 md:py-24 px-4 md:px-8 animate-in fade-in slide-in-from-bottom-8 duration-1000" data-id="element-851" data-mp="mn12j68k7kba">
            <div className="max-w-3xl mx-auto text-center" data-id="element-852" data-mp="dopdh8nccuky">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" data-id="element-853" data-mp="dmcowcn2vlyx">
                <CheckCircle2 size={40} className="text-[#01ced1]" aria-hidden="true" data-id="element-854" data-mp="3q9p3dfp7zf5" />
              </div>
              <h2 className="font-manrope text-[#1a0647] text-3xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-855" data-mp="3ptthfkrbzclh">
                Nice work. The California Privacy module is complete. Your
                family is a little safer today.
              </h2>

              <div className="bg-white/30 p-8 rounded-2xl mt-10 max-w-lg mx-auto backdrop-blur-sm border border-white/40 shadow-sm" data-id="element-856" data-mp="8iaj16urfovs">
                <p className="font-inter text-[#1a0647] text-xl font-bold mb-6" data-id="element-857" data-mp="372shouidzd0v">
                  Share with your family
                </p>
                <button className="bg-[#1a0647] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#2a0a6b] transition-colors min-h-[44px] inline-flex items-center justify-center gap-3 shadow-md w-full sm:w-auto" data-id="element-858" data-mp="3gylu8dw3v6in">
                  <Share2 size={20} aria-hidden="true" data-id="element-859" data-mp="2o9okiz5ymi1i" />
                  Send to a family member
                </button>
              </div>

              <div className="mt-16 text-left max-w-2xl mx-auto" data-id="element-860" data-mp="ban6uvg322zi">
                <Link to="/resources" className="block bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all group no-underline border-2 border-transparent hover:border-[#8528e2]/50" data-id="element-861" data-mp="bawyengs0i7b">
                  <p className="font-manrope text-[#8528e2] font-bold text-sm uppercase tracking-wider mb-3" data-id="element-862" data-mp="g96ur1e8loui">
                    What's Next
                  </p>
                  <div className="flex items-center justify-between" data-id="element-863" data-mp="u7ekopcuvpw6">
                    <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold" data-id="element-864" data-mp="q74dj9dbutfq">
                      Protecting Your Passwords
                    </h3>
                    <div className="w-12 h-12 rounded-full bg-[#f8f8f8] flex items-center justify-center group-hover:bg-[#8528e2] group-hover:text-white transition-colors text-[#1a0647] shadow-sm" data-id="element-865" data-mp="yzpow4empxuj">
                      <ChevronRight size={24} aria-hidden="true" data-id="element-866" data-mp="18yx7p744ukp1" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </section>}
      </main>
    </div>;
}